import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import jwt_decode from "jwt-decode";

import Sidebar from "./Sidebar";
import { useIsMobile } from '../utils/hooks'
import Modal from '../components/Modal'

import { CircleAlert, X } from 'lucide-react'

import OnboardingModal from '../components/OnboardingModal';
import ConnectAccountBanner from '../components/ConnectAccountBanner';
import PageTutorial from '../components/tutorial/PageTutorial';
import { tutorialConfigs, tutorialPageForPath } from '../components/tutorial/tutorialConfig';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import dashboardApi from '../api/dashboardApi';
import marginApi from '../api/marginApi';
import stripeApi from '../api/stripeApi';
import signalApi from '../api/signalApi';
import notificationApi from '../api/notificationApi';
import { useUser } from '../context/UserContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { bootstrapSessionFromLocal, clearPersistedSession, getStoredToken } from '../utils/authStorage';
import WeeklySummaryModal from '../features/signals/components/WeeklySummaryModal';

interface MainLayoutProps {
  children?: React.ReactNode;
}

export default function MainLayout({ children }: MainLayoutProps) {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [showBanner, setShowBanner] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showConnectAccountBanner, setShowConnectAccountBanner] = useState(false);
  const [showWeeklySummaryModal, setShowWeeklySummaryModal] = useState(false);
  const [hasCheckedMetadata, setHasCheckedMetadata] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile()
  const { userMetadata, isLoading: isMetadataLoading, logout, isAdmin } = useUser();
  const { activeBusinessId } = useWorkspace();
  const queryClient = useQueryClient();

  const [isVerified] = useState(() => {
    return sessionStorage.getItem('approval_status') === 'true';
  });

  const handleShowLogout = () => {
    setShowLogoutModal(true)
  }

  const handleLogout = () => {
    logout();
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  const handleCloseBanner = () => {
    setShowConnectAccountBanner(false);
  };

  // Fetch Connected Accounts (still needed for Settings page)

  // Fetch Connected Accounts (still needed for Settings page)
  useQuery({
    queryKey: ['stripeData'],
    queryFn: dashboardApi.getStripeAccounts,
    retry: false,
  });

  useQuery({
    queryKey: ['qboData'],
    queryFn: dashboardApi.getQboAccounts,
    retry: false,
  });

  const { data: weeklySummaryBannerData } = useQuery({
    queryKey: ['weekly-summary-banner', activeBusinessId],
    queryFn: () => signalApi.getWeeklySummary(activeBusinessId || undefined),
    enabled: !!activeBusinessId && !isAdmin && location.pathname === '/home',
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const { data: notificationFeed } = useQuery({
    queryKey: ['notification-feed', activeBusinessId],
    queryFn: () => notificationApi.getFeed(activeBusinessId || undefined),
    enabled: !!activeBusinessId && !isAdmin,
    retry: false,
  });

  useEffect(() => {
    // Only proceed if we haven't checked yet and metadata is ready
    if (!hasCheckedMetadata && !isMetadataLoading && userMetadata) {
      const isJustLoggedIn = sessionStorage.getItem('just_logged_in') === 'true';

      // Refresh bank accounts first, then cashflow & margin in the background on login
      if (isJustLoggedIn) {
        (async () => {
          try {
            console.log("Starting background refresh sequence...");

            // Step 1: Trigger a transaction-only catch-up sync for linked bank accounts.
            // Daily login should not spend a balance refresh.
            await stripeApi.syncAllTransactions(activeBusinessId || undefined).catch(err =>
              console.error("Background transaction sync failed:", err)
            );

            // Step 2: Trigger cashflow refresh (skip if Dashboard already started it)
            const refreshStarted = sessionStorage.getItem('login_cashflow_refresh_started') === 'true';
            if (!refreshStarted) {
              sessionStorage.setItem('login_cashflow_refresh_started', 'true');
              console.log("Triggering cashflow calculation...");
              await dashboardApi.calculateCashflow(activeBusinessId || undefined).catch(err =>
                console.error('Background cashflow refresh failed:', err)
              );
            } else {
              console.log("Cashflow refresh already triggered by Dashboard.");
            }

            console.log("Triggering margin synchronization...");
            await marginApi.syncMarginV2().catch(err =>
              console.error('Background margin refresh failed:', err)
            );

            // Step 3: Invalidate TanStack Query caches so UI components refetch with fresh data
            console.log("Invalidating query caches...");
            queryClient.invalidateQueries({ queryKey: ['cashflowData'] });
            queryClient.invalidateQueries({ queryKey: ['marginV2'] });
            queryClient.invalidateQueries({ queryKey: ['topExpenses'] });
            queryClient.invalidateQueries({ queryKey: ['stripeData'] });
            queryClient.invalidateQueries({ queryKey: ['qboData'] });
            queryClient.invalidateQueries({ queryKey: ['userMetadata'] });
            queryClient.invalidateQueries({ queryKey: ['pageSummary'] });

            console.log("Background refresh sequence complete.");
          } catch (err) {
            console.error('Background refresh sequence failed:', err);
          }
        })();
      }

      // Check if onboarding is incomplete
      const needsOnboarding = !userMetadata.feature_flags.is_onboarding_completed;

      if (needsOnboarding && !isAdmin) {
        // Show persistent banner if not fully onboarded
        setShowConnectAccountBanner(true);

        if (isJustLoggedIn) {
          // Fresh login AND incomplete onboarding -> Show Onboarding Modal
          setTimeout(() => setShowOnboarding(true), 1000);
        }
      }

      // Clear the login flag so reloads don't trigger it again
      sessionStorage.removeItem('just_logged_in');
      setHasCheckedMetadata(true);
    }
  }, [isMetadataLoading, userMetadata, hasCheckedMetadata, isAdmin, activeBusinessId, queryClient]);

  useEffect(() => {
    bootstrapSessionFromLocal();
    const token = getStoredToken()

    if (token) {
      try {
        const decodedToken = jwt_decode<{ exp: number }>(token);
        const isExpired = decodedToken.exp * 1000 < Date.now();

        if (isExpired) {
          clearPersistedSession();
          alert("Session expired. Please log in again.");
          navigate("/");
        }
      } catch (error) {
        console.error("Invalid token format:", error);
        clearPersistedSession();
        navigate("/");
      }
    } else {
      navigate("/");
    }
  }, [navigate]);

  const notificationCount = isAdmin ? 0 : notificationFeed?.unread_count || 0;

  // Determine Active Notification Content
  const getActiveNotification = () => {
    if (!userMetadata) return null;

    // Matches logic in Notification.tsx to ensure we show the "first" message

    // Step 1: Connect Stripe
    if (!userMetadata.connections.linked_stripe) {
      return {
        title: "Step 1/3: Connect your accounts to get started",
        description: "We’ll guide you through a few quick steps to set up cash flow and margin insights.",
        actionLabel: "Connect Accounts",
        onAction: () => navigate('/settings?tab=Accounts') // Use URL params now
      };
    }

    // Step 2: Connect QBO
    if (!userMetadata.connections.linked_qbo) {
      return {
        title: "Step 2/3: Connect QuickBooks to complete setup",
        description: "Bank accounts are connected. Connect QuickBooks to unlock margin, P&L, and performance insights.",
        actionLabel: "Connect QuickBooks",
        onAction: () => navigate('/settings?tab=Accounts') // Use URL params now
      };
    }

    // Step 3: Upload P&L
    if (!userMetadata.feature_flags.available_pl) {
      return {
        title: "Step 3/3: Confirm recent book close and P&L.",
        description: "With your accounts connected, confirm your latest P&L to ensure accurate cash flow and margin insights.",
        actionLabel: "Confirm Financials",
        onAction: () => navigate('/settings?tab=Notification')
      };
    }

    // If all done, return null -> Banner will NOT show
    return null;
  };

  const activeNotification = getActiveNotification();
  const tutorialPageKey = tutorialPageForPath(location.pathname);
  const hasPendingPageTutorial = Boolean(
    !isAdmin &&
    userMetadata?.feature_flags?.is_onboarding_completed &&
    tutorialPageKey &&
    ((userMetadata.tutorial_state?.completed_pages?.[tutorialPageKey]?.version || 0) < tutorialConfigs[tutorialPageKey].version)
  );
  // Show banner if:
  // 1. showConnectAccountBanner is true (user hasn't manually closed it)
  // 2. We have an active notification to show (not fully onboarded)
  // 3. VISIBILITY RULE: 
  //    - Show on Dashboard (not /settings)
  //    - XOR Show on Settings ONLY if tab=Notification


  const shouldShowBanner = showConnectAccountBanner && activeNotification &&
    (!location.pathname.includes('/settings')) && !isAdmin && false;
  const shouldShowWeeklySummaryBanner =
    !isAdmin &&
    !!activeBusinessId &&
    location.pathname === '/home' &&
    !hasPendingPageTutorial &&
    !!weeklySummaryBannerData &&
    weeklySummaryBannerData.banner_dismissed !== true;

  return (
    <div className="flex h-[100dvh] overflow-y-hidden">
      <Sidebar
        handleShowLogout={handleShowLogout}
        onCollapseChange={setIsSidebarCollapsed}
        isMobile={isMobile}
        notificationCount={notificationCount}
        isAdmin={isAdmin}
      />

      {/* Onboarding Modal */}
      {showOnboarding && <OnboardingModal onComplete={handleOnboardingComplete} />}

      {showLogoutModal &&
        <Modal onClose={() => setShowLogoutModal(false)}>
          <div className="mb-4">Confirm Logout</div>
          <div className="space-x-4">
            <button className="button-1 !w-48" onClick={handleLogout}>Logout</button>
            <button className="button-2 !w-48" onClick={() => setShowLogoutModal(false)}>Cancel</button>
          </div>
        </Modal>}

      <WeeklySummaryModal
        open={showWeeklySummaryModal}
        businessId={activeBusinessId}
        onClose={() => setShowWeeklySummaryModal(false)}
      />

      <div
        className={`
          transition-all duration-200
          flex flex-col relative w-full
          overflow-visible
          ${isMobile ? "mt-[50px]" :
            isSidebarCollapsed
              ? "ml-[65px]"
              : "ml-[200px]"}
          `}
        style={{
          width: !isMobile
            ? isSidebarCollapsed
              ? "calc(100dvw - 65px)"
              : "calc(100dvw - 200px)"
            : ""
        }}
      >
        {/* Waitlist Banner */}
        {(!isVerified && showBanner) &&
          <div className="w-full flex justify-center absolute top-2 z-[1000]">
            <div className="justify-between flex items-center w-4/5 bg-gray-800 p-2 rounded text-white text-xs">
              <div className="flex gap-1">
                <CircleAlert className="w-4 h-4" /><span>Thanks for signing up! You're on the waitlist. We'll notify you as soon as it's your turn.</span>
              </div>
              <button onClick={() => setShowBanner(false)}><X className="w-4 h-4" /></button>
            </div>
          </div>}

        {shouldShowBanner && (
          <div className="absolute top-6 left-6 right-6 z-50">
            <ConnectAccountBanner
              onClose={handleCloseBanner}
              title={activeNotification!.title}
              description={activeNotification!.description}
              actionLabel={activeNotification!.actionLabel}
              onAction={activeNotification!.onAction}
            />
          </div>
        )}

        {shouldShowWeeklySummaryBanner && (
          <div className="absolute left-3 right-3 top-3 z-50 sm:left-6 sm:right-6 sm:top-6">
            <ConnectAccountBanner
              onClose={async () => {
                try {
                  await signalApi.dismissWeeklySummary(activeBusinessId || undefined);
                  queryClient.invalidateQueries({ queryKey: ['weekly-summary-banner', activeBusinessId] });
                  queryClient.invalidateQueries({ queryKey: ['weekly-summary', activeBusinessId] });
                } catch (error) {
                  console.error('Failed to dismiss weekly summary banner:', error);
                }
              }}
              title="Weekly summary is ready"
              description="Check last week’s cash and margin, then review this week’s obligations and signals."
              actionLabel="See actions"
              onAction={() => setShowWeeklySummaryModal(true)}
              variant="compact"
            />
          </div>
        )}

        <PageTutorial />

        {children}
      </div>

    </div>
  );
}
